import { type PayloadAction } from "@reduxjs/toolkit"
import { isEqual } from "lodash"
import type { ReduxState } from "model/state"
import type { DateOverride } from "../helpers/date-override"
import { createPanelSlice, type PanelRootState } from "./create-panel-slice"
import { makeDuplicateFlowReducers, makeTemplateChartReducers, type TemplateChartSettings } from "./template-chart-settings"

export interface GraphState {
  selectedTemplate: string
  disabledItems: Record<string, boolean>
  // Per-template chart category, keyed by template name (factor-sensitivity splits
  // "factor-sensitivity-{predicted|historical}"); defaults live at the consumer call site.
  templateCharts: Partial<Record<string, TemplateChartSettings>>
  // Per-panel date range that supersedes the dashboard's dates when present.
  dateOverride: DateOverride | null
  isPreferencesLoaded: boolean
  /** Transient (never persisted): set by clonePanelState so the duplicate's sync hook
   *  persists the copied state once, then cleared via clearPendingClonePersist. */
  pendingClonePersist?: boolean
  showMetricsSelector: boolean
  duplicateFromViewName: string | null
  duplicateFromTemplateId: string | null
  duplicateFromViewMetrics: string[] | null
  duplicateReturnViewId: string | null
  /** Metrics added via the add-series surface, without switching templates. */
  addedMetrics: string[]
  /** Session-only color slot each plotted series holds, so selection changes never reshuffle colors. */
  seriesColorSlots: Record<string, number>
}

/** Keyed by dashboardId at the top, panelId at the inner level. */
export type GraphRootState = PanelRootState<GraphState>

export const DEFAULT_GRAPH_STATE: GraphState = {
  selectedTemplate: "performance",
  disabledItems: { residual: true },
  templateCharts: {},
  dateOverride: null,
  isPreferencesLoaded: false,
  showMetricsSelector: false,
  duplicateFromViewName: null,
  duplicateFromTemplateId: null,
  duplicateFromViewMetrics: null,
  duplicateReturnViewId: null,
  addedMetrics: [],
  seriesColorSlots: {},
}

// Preference subtype base; the sync hook and data hook both key off this.
export const GRAPH_PREFERENCE_BASE = "graph"

/** Persisted fields — single source for the setPreferences whitelist (here) and the sync hook's diff baseline. */
export const GRAPH_PREFERENCE_FIELDS = ["selectedTemplate", "disabledItems", "templateCharts", "dateOverride"] as const

/** Co-located access to a panel's raw slot (undefined when absent); callers coalesce to DEFAULT_GRAPH_STATE. */
export const selectGraphPanel = (
  state: ReduxState,
  dashboardId: string,
  panelKey: string,
): GraphState | undefined => state.dashboard.graph[dashboardId]?.[panelKey]

// Memoized per source panel object so repeat calls for unchanged state return a stable reference.
const syncOverrideCache = new WeakMap<GraphState, GraphState>()

// Stable fallback so a no-op lookup never allocates a fresh (triggers-a-rerender) array.
const EMPTY_ADDED_DISABLED_ITEM_IDS: string[] = []

/** IDs that are both in addedMetrics and have an explicit disabledItems entry — addMetric's own
 *  session-only scaffolding (the `false` it writes on add, or `true` from a subsequent hide),
 *  mirroring addedMetrics itself. disabledItems/addedMetrics can be absent on a partial test
 *  fixture (e.g. a bare preference-load mock). */
function findAddedDisabledItemIds(
  disabledItems: GraphState["disabledItems"] | undefined,
  addedMetrics: GraphState["addedMetrics"] | undefined,
): string[] {
  if (!addedMetrics?.length) return EMPTY_ADDED_DISABLED_ITEM_IDS
  const ids = addedMetrics.filter((metricId) => Object.hasOwn(disabledItems ?? {}, metricId))
  return ids.length > 0 ? ids : EMPTY_ADDED_DISABLED_ITEM_IDS
}

/** Drops the given ids' entries from disabledItems. Call wherever addedMetrics is reset/dropped
 *  without carrying along the disabledItems entry addMetric wrote for it (sync-to-persistence,
 *  and cloning into a new panel) — addedMetrics is deliberately excluded from
 *  GRAPH_PREFERENCE_FIELDS, so without this the leftover entry outlives it: it still reads as an
 *  explicit selection (`hasExplicitSelection` in graph/helpers.js), so the item plots or stays
 *  hidden forever regardless of rank (bugbot findings on PR #4401, reviews 5491317123 and
 *  5509287996). Returns the same disabledItems reference when there's nothing to strip, so a
 *  no-op lookup costs no allocation.
 */
function stripAddedDisabledItems(
  disabledItems: GraphState["disabledItems"],
  addedDisabledItemIds: string[],
): GraphState["disabledItems"] {
  if (addedDisabledItemIds.length === 0) return disabledItems
  return Object.fromEntries(
    Object.entries(disabledItems).filter(([itemId]) => !addedDisabledItemIds.includes(itemId)),
  )
}

/** Sync-only view of a panel's slot: substitutes a mid-duplicate-flow "custom" selectedTemplate
 *  with duplicateReturnViewId so it's never diffed/persisted on its own, and strips addMetric's
 *  session-only disabledItems scaffolding (see stripAddedDisabledItems) before it reaches
 *  persistence. */
export const selectGraphPanelForSync = (
  state: ReduxState,
  dashboardId: string,
  panelKey: string,
): GraphState | undefined => {
  const panel = selectGraphPanel(state, dashboardId, panelKey)
  if (!panel) return panel

  const pendingTemplateReturn = panel.duplicateReturnViewId && panel.selectedTemplate === "custom"
    ? panel.duplicateReturnViewId
    : null
  const addedDisabledItemIds = findAddedDisabledItemIds(panel.disabledItems, panel.addedMetrics)

  if (!pendingTemplateReturn && addedDisabledItemIds.length === 0) {
    return panel
  }

  const cached = syncOverrideCache.get(panel)
  if (cached) return cached

  const overridden: GraphState = {
    ...panel,
    ...(pendingTemplateReturn && { selectedTemplate: pendingTemplateReturn }),
    ...(addedDisabledItemIds.length > 0 && {
      disabledItems: stripAddedDisabledItems(panel.disabledItems, addedDisabledItemIds),
    }),
  }
  syncOverrideCache.set(panel, overridden)
  return overridden
}

export const graphSlice = createPanelSlice({
  name: "dashboard/graph",
  defaultState: DEFAULT_GRAPH_STATE,
  preferenceFields: GRAPH_PREFERENCE_FIELDS,
  // Clone disabledItems / templateCharts / addedMetrics so dashboards don't share the default object reference.
  freshOverrides: (defaultState) => ({
    disabledItems: { ...defaultState.disabledItems },
    templateCharts: {},
    addedMetrics: [...defaultState.addedMetrics],
    seriesColorSlots: {},
  }),
  cloneOverrides: (sourceState) => ({
    // Strip addMetric's session-only disabledItems scaffolding (see stripAddedDisabledItems) —
    // addedMetrics resets below, so a leftover entry would plot/hide the item forever on the
    // clone regardless of rank, with no addedMetrics membership left to justify it.
    disabledItems: stripAddedDisabledItems(
      { ...sourceState.disabledItems },
      findAddedDisabledItemIds(sourceState.disabledItems, sourceState.addedMetrics),
    ),
    templateCharts: { ...sourceState.templateCharts },
    // Session-only: reset, not copied — same reasoning as duplicateFromViewMetrics below.
    addedMetrics: [],
    seriesColorSlots: {},
    showMetricsSelector: false,
    duplicateFromViewName: null,
    duplicateFromTemplateId: null,
    duplicateFromViewMetrics: null,
    duplicateReturnViewId: null,
  }),
  reducers: ({ ensure, initInstance }) => ({
    ...makeTemplateChartReducers<GraphState>(ensure),
    ...makeDuplicateFlowReducers<GraphState>(ensure),
    setSelectedTemplate(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, template: string }>,
    ) {
      const { dashboardId, panelId, template } = action.payload
      ensure(state, dashboardId, panelId).selectedTemplate = template
    },

    // Seed the module-config template as the panel baseline. No-ops once the instance exists,
    // so a saved preference that loads first is never overwritten (mirrors the pre-RTK reducer
    // initialState: config template was the initial value, saved prefs overrode it).
    initFromModuleConfig(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, selectedTemplate?: string, dateOverride?: DateOverride | null }>,
    ) {
      const { dashboardId, panelId, selectedTemplate, dateOverride } = action.payload
      const overrides: Partial<GraphState> = {}
      if (selectedTemplate != null) overrides.selectedTemplate = selectedTemplate
      if (dateOverride !== undefined) overrides.dateOverride = dateOverride
      initInstance(state, dashboardId, panelId, overrides)
    },

    setDisabledItems(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, itemId: string, defaultDisabled?: boolean }>,
    ) {
      const { dashboardId, panelId, itemId, defaultDisabled } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      const currentState = instance.disabledItems[itemId]
      instance.disabledItems[itemId] = currentState === undefined ? !defaultDisabled : !currentState
    },

    setOnlyItem(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, itemId: string, allItemIds: string[] }>,
    ) {
      const { dashboardId, panelId, itemId, allItemIds } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      instance.disabledItems = Object.fromEntries(
        allItemIds.map((id) => [id, id !== itemId]),
      )
    },

    enableItems(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, itemIds: string[] }>,
    ) {
      const { dashboardId, panelId, itemIds } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      Object.assign(
        instance.disabledItems,
        Object.fromEntries(itemIds.map((id) => [id, false])),
      )
    },

    setDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, dateOverride: DateOverride }>,
    ) {
      const { dashboardId, panelId, dateOverride } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = dateOverride
    },

    clearDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string }>,
    ) {
      const { dashboardId, panelId } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = null
    },

    // Unconditional by design -- callers are responsible for not dispatching this on a no-op reselect.
    resetAddedMetrics(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string }>,
    ) {
      const { dashboardId, panelId } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      // Also drop disabledItems for cleared ids, or a re-added metric renders hidden from a stale hide.
      for (const metricId of instance.addedMetrics) {
        Reflect.deleteProperty(instance.disabledItems, metricId)
      }
      instance.addedMetrics = []
    },

    // Appends a metric id; a defensive no-op dedupe if it's already present.
    addMetric(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, metricId: string }>,
    ) {
      const { dashboardId, panelId, metricId } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      if (!instance.addedMetrics.includes(metricId)) {
        instance.addedMetrics.push(metricId)
      }
      // Explicit, so an addition ranked outside the default plotted set still plots.
      instance.disabledItems[metricId] = false
    },

    setSeriesColorSlots(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, seriesColorSlots: Record<string, number> }>,
    ) {
      const { dashboardId, panelId, seriesColorSlots } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      if (isEqual(instance.seriesColorSlots, seriesColorSlots)) return
      instance.seriesColorSlots = seriesColorSlots
    },
  }),
})

export default graphSlice.reducer
